/**
 * pdfPageExtractor.ts
 *
 * Extracts thumbnail data-URLs and text content from every page of a loaded
 * PDF document (pdfjs-dist PDFDocumentProxy). Used by the presentation
 * timeline editor to build initial slots from an uploaded PDF.
 */

export interface PdfPageInfo {
  pageNumber: number;
  thumbnailDataUrl: string;
  textContent: string;
}

const THUMB_HEIGHT = 120;

/**
 * Extract a small thumbnail (data-URL) and full text for a single page.
 * Uses an offscreen canvas so the DOM is never touched.
 */
async function extractPage(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdf: any,
  pageNum: number,
): Promise<PdfPageInfo> {
  const page = await pdf.getPage(pageNum);

  // Thumbnail
  const baseVp = page.getViewport({ scale: 1 });
  const scale = THUMB_HEIGHT / baseVp.height;
  const vp = page.getViewport({ scale });
  const canvas = new OffscreenCanvas(Math.floor(vp.width), Math.floor(vp.height));
  const ctx = canvas.getContext('2d')!;
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  // TS: BlobPropertyBag bevat niet overal `quality`, maar de browser runtime ondersteunt dit wel.
  const blob = await (canvas as unknown as { convertToBlob(opts?: BlobPropertyBag | any): Promise<Blob> }).convertToBlob({ type: 'image/webp', quality: 0.6 } as any);
  const thumbnailDataUrl = await blobToDataUrl(blob);

  // Text
  const tc = await page.getTextContent();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const textContent = tc.items.map((it: any) => it.str).join(' ').trim();

  return { pageNumber: pageNum, thumbnailDataUrl, textContent };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Extract thumbnails + text for all pages, yielding progress per page.
 * Returns an array ordered by pageNumber.
 */
export async function extractAllPages(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdf: any,
  onProgress?: (done: number, total: number) => void,
): Promise<PdfPageInfo[]> {
  const total: number = pdf.numPages;
  const results: PdfPageInfo[] = [];

  // Process in small batches to keep the UI responsive
  const BATCH = 4;
  for (let start = 1; start <= total; start += BATCH) {
    const end = Math.min(start + BATCH - 1, total);
    const batch = [];
    for (let p = start; p <= end; p++) batch.push(extractPage(pdf, p));
    const pages = await Promise.all(batch);
    results.push(...pages);
    onProgress?.(results.length, total);
  }

  return results.sort((a, b) => a.pageNumber - b.pageNumber);
}
